import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  adminStats,
  adminWaitlist,
  adminContact,
  adminConcepts,
  adminToggleShowcase,
  adminDownloadExport,
  clearAdminSession,
} from '@/lib/api';
import { useForceLightTheme } from '@/lib/theme';
import { VERTICALS } from '@/lib/verticals';
import { fr as FR_DICT } from '@/lib/i18n/fr';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Download, LogOut, Star, StarOff, Sparkles, BarChart3, CreditCard, Users, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AnalyticsTab from '@/pages/admin/AnalyticsTab';
import PaymentsTab from '@/pages/admin/PaymentsTab';
import CreatorsTab from '@/pages/admin/CreatorsTab';
import AppsTab from '@/pages/admin/AppsTab';

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso || '';
  }
}

function StatCard({ label, value, hint, accent = '#0A0E1A' }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[var(--om-border)] bg-white p-5"
      data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div
        aria-hidden
        className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-40 blur-2xl"
        style={{ background: `radial-gradient(closest-side, ${accent}44, transparent 70%)` }}
      />
      <div className="relative text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
        {label}
      </div>
      <div className="relative mt-2 text-3xl font-extrabold tabular-nums text-[var(--om-ink)]">
        {value}
      </div>
      {hint && (
        <div className="relative mt-1 text-xs text-[var(--om-muted)]">{hint}</div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  useForceLightTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('waitlist');
  const [waitlist, setWaitlist] = useState({ items: [], total: 0, page: 1 });
  const [contact, setContact] = useState({ items: [], total: 0, page: 1 });
  const [concepts, setConcepts] = useState({ items: [], total: 0, page: 1 });
  const [conceptsVertical, setConceptsVertical] = useState('');
  const [loading, setLoading] = useState({ wl: false, ct: false, cc: false });
  const [selectedConcept, setSelectedConcept] = useState(null);

  useEffect(() => {
    document.title = 'Admin — OpenMeta';
    (async () => {
      try {
        const s = await adminStats();
        setStats(s);
      } catch {
        // token expired → protected route re-check on next reload
      }
    })();
  }, []);

  const loadWaitlist = async (page = 1) => {
    setLoading((l) => ({ ...l, wl: true }));
    try {
      const r = await adminWaitlist({ page, limit: 50 });
      setWaitlist({ items: r.items, total: r.total, page: r.page });
    } finally {
      setLoading((l) => ({ ...l, wl: false }));
    }
  };
  const loadContact = async (page = 1) => {
    setLoading((l) => ({ ...l, ct: true }));
    try {
      const r = await adminContact({ page, limit: 50 });
      setContact({ items: r.items, total: r.total, page: r.page });
    } finally {
      setLoading((l) => ({ ...l, ct: false }));
    }
  };
  const loadConcepts = async (page = 1, vertical = '') => {
    setLoading((l) => ({ ...l, cc: true }));
    try {
      const r = await adminConcepts({ page, limit: 20, vertical: vertical || undefined });
      setConcepts({ items: r.items, total: r.total, page: r.page });
    } finally {
      setLoading((l) => ({ ...l, cc: false }));
    }
  };

  useEffect(() => { loadWaitlist(1); }, []);
  useEffect(() => {
    if (tab === 'contact' && contact.items.length === 0) loadContact(1);
    if (tab === 'concepts' && concepts.items.length === 0) loadConcepts(1, conceptsVertical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleToggleShowcase = async (concept) => {
    const next = !concept.is_showcase;
    try {
      await adminToggleShowcase(concept.id, next);
      setConcepts((c) => ({
        ...c,
        items: c.items.map((it) => (it.id === concept.id ? { ...it, is_showcase: next } : it)),
      }));
      toast.success(next ? 'Concept ajouté à la vitrine.' : 'Concept retiré de la vitrine.');
      // Refresh stats
      const s = await adminStats();
      setStats(s);
    } catch {
      toast.error('Impossible de mettre à jour ce concept.');
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin/login', { replace: true });
  };

  const handleExport = async (kind) => {
    try {
      await adminDownloadExport(kind);
      toast.success(`Export ${kind === 'waitlist' ? 'waitlist' : 'contact'} téléchargé.`);
    } catch {
      toast.error('Export impossible pour le moment.');
    }
  };

  const verticalOptions = useMemo(
    () => [
      { code: '', label: 'Toutes verticales', short: 'Toutes verticales', color: '#0A0E1A' },
      ...VERTICALS.map((v) => ({
        ...v,
        label: FR_DICT.verticals[v.code]?.label || v.code,
        short: FR_DICT.verticals[v.code]?.short || v.code,
      })),
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-[var(--om-bg)]" data-testid="page-admin-dashboard">
      <header className="border-b border-[var(--om-border)] bg-white/80 backdrop-blur">
        <div className="om-container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-mark.png" alt="OpenMeta" className="h-8 w-8" />
            <div className="text-sm font-bold tracking-tight text-[var(--om-ink)]">
              OpenMeta <span className="text-[var(--om-muted)]">/ Admin</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--om-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--om-ink)] hover:bg-[var(--om-bg)]"
          >
            <LogOut size={14} strokeWidth={2.4} />
            Déconnexion
          </button>
        </div>
      </header>

      <main className="om-container py-10">
        <h1 className="om-h1 text-[32px] sm:text-[40px] text-[var(--om-ink)]">Tableau de bord</h1>
        <p className="mt-3 text-sm text-[var(--om-muted)]">Vue d'ensemble de l'activité OpenMeta.</p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Waitlist"
            value={stats?.waitlist?.total ?? '—'}
            hint="Inscrits early access"
            accent="#10B981"
          />
          <StatCard
            label="Messages"
            value={stats?.contact?.total ?? '—'}
            hint={`${stats?.contact?.unread ?? 0} non lus`}
            accent="#F97316"
          />
          <StatCard
            label="Concepts"
            value={stats?.concepts?.total ?? '—'}
            hint={`${stats?.concepts?.showcases ?? 0} en vitrine`}
            accent="#8B5CF6"
          />
        </div>

        <div className="mt-10">
          <Tabs value={tab} onValueChange={setTab} data-testid="admin-tabs">
            <TabsList className="flex w-full flex-wrap gap-1 rounded-full bg-white p-1 shadow-[0_1px_0_rgba(10,14,26,0.06)]">
              <TabsTrigger value="waitlist" data-testid="admin-tab-waitlist" className="rounded-full data-[state=active]:bg-[var(--om-ink)] data-[state=active]:text-white">
                Waitlist ({waitlist.total})
              </TabsTrigger>
              <TabsTrigger value="contact" data-testid="admin-tab-contact" className="rounded-full data-[state=active]:bg-[var(--om-ink)] data-[state=active]:text-white">
                Messages ({contact.total || stats?.contact?.total || 0})
              </TabsTrigger>
              <TabsTrigger value="concepts" data-testid="admin-tab-concepts" className="rounded-full data-[state=active]:bg-[var(--om-ink)] data-[state=active]:text-white">
                Concepts ({concepts.total || stats?.concepts?.total || 0})
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-full data-[state=active]:bg-[var(--om-ink)] data-[state=active]:text-white">
                <BarChart3 size={12} strokeWidth={2.4} className="mr-1.5" /> Analytics
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="admin-tab-payments" className="rounded-full data-[state=active]:bg-[var(--om-ink)] data-[state=active]:text-white">
                <CreditCard size={12} strokeWidth={2.4} className="mr-1.5" /> Paiements
              </TabsTrigger>
              <TabsTrigger value="creators" data-testid="admin-tab-creators" className="rounded-full data-[state=active]:bg-[var(--om-ink)] data-[state=active]:text-white">
                <Users size={12} strokeWidth={2.4} className="mr-1.5" /> Créateurs
              </TabsTrigger>
              <TabsTrigger value="apps" data-testid="admin-tab-apps" className="rounded-full data-[state=active]:bg-[var(--om-ink)] data-[state=active]:text-white">
                <LayoutTemplate size={12} strokeWidth={2.4} className="mr-1.5" /> Apps
              </TabsTrigger>
            </TabsList>

            {/* Waitlist */}
            <TabsContent value="waitlist" className="mt-6">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                  {waitlist.items.length} affichés / {waitlist.total} total
                </div>
                <button
                  type="button"
                  onClick={() => handleExport('waitlist')}
                  data-testid="admin-export-waitlist"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--om-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--om-ink)] hover:bg-[var(--om-bg)]"
                >
                  <Download size={14} strokeWidth={2.4} /> Export CSV
                </button>
              </div>
              <div className="mt-4 overflow-hidden rounded-2xl border border-[var(--om-border)] bg-white">
                <table className="w-full text-sm" data-testid="admin-waitlist-table">
                  <thead className="border-b border-[var(--om-border)] bg-[var(--om-bg)] text-left text-[11px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">
                    <tr>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Source</th>
                      <th className="px-4 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading.wl && waitlist.items.length === 0 && (
                      <tr><td colSpan={3} className="px-4 py-8 text-center text-[var(--om-muted)]">Chargement…</td></tr>
                    )}
                    {!loading.wl && waitlist.items.length === 0 && (
                      <tr><td colSpan={3} className="px-4 py-8 text-center text-[var(--om-muted)]">Aucune entrée pour le moment.</td></tr>
                    )}
                    {waitlist.items.map((row, i) => (
                      <tr key={row.id || i} className="border-t border-[var(--om-border)]" data-testid={`admin-waitlist-row-${i}`}>
                        <td className="px-4 py-3 font-medium text-[var(--om-ink)]">{row.email}</td>
                        <td className="px-4 py-3 text-[var(--om-muted)]">{row.source || '—'}</td>
                        <td className="px-4 py-3 tabular-nums text-[var(--om-muted)]">{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Contact */}
            <TabsContent value="contact" className="mt-6">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                  {contact.items.length} affichés / {contact.total} total
                </div>
                <button
                  type="button"
                  onClick={() => handleExport('contact')}
                  data-testid="admin-export-contact"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--om-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--om-ink)] hover:bg-[var(--om-bg)]"
                >
                  <Download size={14} strokeWidth={2.4} /> Export CSV
                </button>
              </div>
              <div className="mt-4 space-y-3" data-testid="admin-contact-list">
                {loading.ct && contact.items.length === 0 && (
                  <div className="rounded-2xl border border-[var(--om-border)] bg-white px-4 py-8 text-center text-[var(--om-muted)]">Chargement…</div>
                )}
                {!loading.ct && contact.items.length === 0 && (
                  <div className="rounded-2xl border border-[var(--om-border)] bg-white px-4 py-8 text-center text-[var(--om-muted)]">Aucun message pour le moment.</div>
                )}
                {contact.items.map((m, i) => (
                  <details key={m.id || i} className="rounded-2xl border border-[var(--om-border)] bg-white" data-testid={`admin-contact-row-${i}`}>
                    <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                            style={{
                              color: '#F97316',
                              background: '#F9731618',
                            }}
                          >
                            {m.subject_label || m.subject}
                          </span>
                          <span className="truncate text-sm font-semibold text-[var(--om-ink)]">
                            {m.name}
                          </span>
                          <span className="truncate text-xs text-[var(--om-muted)]">
                            &lt;{m.email}&gt;
                          </span>
                        </div>
                      </div>
                      <span className="shrink-0 text-xs tabular-nums text-[var(--om-muted)]">{formatDate(m.created_at)}</span>
                    </summary>
                    <div className="whitespace-pre-wrap border-t border-[var(--om-border)] px-5 py-4 text-sm leading-relaxed text-[var(--om-ink-soft)]">
                      {m.message}
                    </div>
                  </details>
                ))}
              </div>
            </TabsContent>

            {/* Concepts */}
            <TabsContent value="concepts" className="mt-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Filtre :</span>
                {verticalOptions.map((v) => (
                  <button
                    key={v.code || 'all'}
                    type="button"
                    data-testid={`admin-concept-filter-${v.code || 'all'}`}
                    onClick={() => { setConceptsVertical(v.code); loadConcepts(1, v.code); }}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      conceptsVertical === v.code
                        ? 'border-transparent bg-[var(--om-ink)] text-white'
                        : 'border-[var(--om-border)] bg-white text-[var(--om-ink)] hover:bg-[var(--om-bg)]'
                    }`}
                  >
                    {v.color !== '#0A0E1A' && (
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: v.color }} />
                    )}
                    {v.label || v.short}
                  </button>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-concepts-grid">
                {loading.cc && concepts.items.length === 0 && (
                  <div className="col-span-full rounded-2xl border border-[var(--om-border)] bg-white p-8 text-center text-[var(--om-muted)]">Chargement…</div>
                )}
                {!loading.cc && concepts.items.length === 0 && (
                  <div className="col-span-full rounded-2xl border border-[var(--om-border)] bg-white p-8 text-center text-[var(--om-muted)]">Aucun concept pour ce filtre.</div>
                )}
                {concepts.items.map((c, i) => {
                  const v = VERTICALS.find((vv) => vv.code === c.vertical);
                  const short = FR_DICT.verticals[c.vertical]?.short || c.vertical;
                  const accent = v?.color || '#0A0E1A';
                  return (
                    <div
                      key={c.id}
                      data-testid={`admin-concept-card-${i}`}
                      className="flex flex-col rounded-2xl border border-[var(--om-border)] bg-white p-5"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: accent, background: `${accent}18` }}
                        >
                          {short}
                        </span>
                        {c.is_showcase && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">
                            <Sparkles size={10} strokeWidth={2.6} /> Vitrine
                          </span>
                        )}
                        <span className="ml-auto text-[10px] tabular-nums text-[var(--om-muted-2)]">{formatDate(c.created_at)}</span>
                      </div>
                      <h3 className="mt-3 truncate text-base font-bold text-[var(--om-ink)]">{c.app_name}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-[var(--om-muted)]">{c.pitch}</p>
                      <div className="mt-4 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedConcept(c)}
                          data-testid={`admin-concept-view-${i}`}
                          className="flex-1 rounded-full border border-[var(--om-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:bg-[var(--om-bg)]"
                        >
                          Voir le détail
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleShowcase(c)}
                          data-testid={`admin-concept-toggle-${i}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
                            c.is_showcase
                              ? 'bg-amber-500 text-white'
                              : 'border border-[var(--om-border)] bg-white text-[var(--om-ink)] hover:bg-[var(--om-bg)]'
                          }`}
                        >
                          {c.is_showcase ? <StarOff size={12} strokeWidth={2.4} /> : <Star size={12} strokeWidth={2.4} />}
                          {c.is_showcase ? 'Retirer vitrine' : 'Ajouter vitrine'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* Analytics (Phase 5) */}
            <TabsContent value="analytics" className="mt-6">
              <AnalyticsTab />
            </TabsContent>

            {/* Payments (Phase 6) */}
            <TabsContent value="payments" className="mt-6">
              <PaymentsTab />
            </TabsContent>

            {/* Creators (Phase 8) */}
            <TabsContent value="creators" className="mt-6">
              <CreatorsTab />
            </TabsContent>

            {/* Apps (Phase 10) */}
            <TabsContent value="apps" className="mt-6">
              <AppsTab />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Dialog open={!!selectedConcept} onOpenChange={(o) => !o && setSelectedConcept(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto rounded-3xl">
          {selectedConcept && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selectedConcept.app_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm text-[var(--om-ink-soft)]">
                <div className="text-[var(--om-muted)]">{selectedConcept.pitch}</div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Description utilisateur</div>
                  <div className="mt-1 whitespace-pre-wrap">{selectedConcept.description}</div>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Cibles</div>
                  <ul className="mt-1 list-inside list-disc">
                    {(selectedConcept.target_users || []).map((u, i) => <li key={i}>{u}</li>)}
                  </ul>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Modules ({(selectedConcept.modules || []).length})</div>
                  <ul className="mt-1 space-y-2">
                    {(selectedConcept.modules || []).map((m, i) => (
                      <li key={i}>
                        <span className="font-semibold">{m.name}</span>
                        {m.ai_powered && <span className="ml-2 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700">IA</span>}
                        <div className="text-[var(--om-muted)]">{m.description}</div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Écrans clés ({(selectedConcept.key_screens || []).length})</div>
                  <ul className="mt-1 space-y-2">
                    {(selectedConcept.key_screens || []).map((s, i) => (
                      <li key={i}>
                        <span className="font-semibold">{s.name}</span>
                        <div className="text-[var(--om-muted)]">{s.description}</div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Blocs no-code</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(selectedConcept.nocode_blocks || []).map((b, i) => (
                      <span key={i} className="rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-2 py-0.5 text-xs">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-[var(--om-muted-2)]">
                  Temps estimé : {selectedConcept.estimated_build_time}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
